import React, { useMemo } from "react";
import { Control, FieldErrors } from "react-hook-form";
import { TextInputProps } from "react-native";

import TextInputFieldView, {
  makeNoteFieldRenderer,
} from "./TextInputFieldView";
import { IForm } from "@/features/notes/type";

import { useTheme } from "@/providers/ThemeProvider";

/**
 * Props interface for TextInputField container
 */
interface Props {
  loading: boolean;
  control: Control<IForm>;
  errors: FieldErrors<IForm>;
  isSmallScreen: boolean;
  InputTextField: React.ComponentType<TextInputProps>;
}

/**
 * Container component for text input field with theme support
 * @param root0 - Component props
 * @param root0.control - React Hook Form control object for form state management
 * @param root0.loading - Whether note submission is in progress
 * @param root0.errors - Form validation errors object
 * @param root0.isSmallScreen - Whether device has small screen size
 * @param root0.InputTextField - Text input component to use (regular or bottom sheet compatible)
 */
const TextInputFieldContainer = ({
  control,
  loading,
  errors,
  isSmallScreen,
  InputTextField,
}: Props) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Memoized because `Controller` re-renders the field whenever `render`
  // changes identity — which is why this used to be a `useCallback` inside the
  // View.
  const renderInput = useMemo(
    () =>
      makeNoteFieldRenderer({
        loading,
        errors,
        isSmallScreen,
        isDark,
        InputTextField,
      }),
    [loading, errors, isSmallScreen, isDark, InputTextField]
  );

  return <TextInputFieldView control={control} renderInput={renderInput} />;
};

export default TextInputFieldContainer;
